/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2023
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/**
 * @class GridCellExpansionHelper
 * @classdesc This class determines a cell's expansion context info such as the number of expanded children, its use value, etc.
 * 1. Determines if a cell is expanded. If the is expanded then recursively walk through the data set to calculate the number of expanded children.
 *
 * The recursive calls terminate when it reaches a data row cell that have the same parent use value as the expanded cell's parent use value.
 * This is because this cell is a sibling of the expanding cell and not a child of the expanded cell. Any data rows onwards do not need to be searched.
 *
 * Make use of unique numeric values represent use values and parent use values to improve the speed of string comparisions.
 * Comparing numbers are faster than comparing large strings, these values get compare repetitively when search for children.
 */
define([
	'../CrosstabConstants'
], (
	CrosstabConstants
) => {
	const USE_VALUE = 'useValue';
	const USE_VALUE_ID = 'uId';
	const PARENT_USE_VALUE = 'parentUseValue';
	const PARENT_USE_VALUE_ID = 'puId';

	const _getUseValueId = (value) => {
		const useValues = (value || '').split(',');
		return useValues.length > 0 && useValues[useValues.length -1];
	};

	/*
	 * Internal class containing the context needed to iterate over the set of data rows to collect expanded children for the expanded cell.
	 */
	class GridCellExpandContext {
		constructor({ cellInfo, gridInfo, crosstabGridCellRenderers, isRowEdge }) {
			const { currentRow, currentCol, currentCellKey, dataValue, colSpan, rowSpan, columnEdgeMaxRowIndex, rowEdgeMaxColumnIndex, resizeKey } = cellInfo;
			const { dataProvider, expandedRootMap, expandedLeafMap } = gridInfo;
			this.crosstabGridCellRenderers = crosstabGridCellRenderers;
			this.isRowEdge = isRowEdge;
			const ancestor_or_self_useValueId = this.crosstabGridCellRenderers.getDataCellValue(dataValue, USE_VALUE_ID);
			this.cellToBeExpanded = {
				currentRow,
				currentCol,
				colSpan,
				rowSpan,
				currentCellKey,
				useValueId: _getUseValueId(ancestor_or_self_useValueId),
				parentUseValueId: this.crosstabGridCellRenderers.getDataCellValue(dataValue, PARENT_USE_VALUE_ID)
			};
			this.columnEdgeMaxRowIndex = columnEdgeMaxRowIndex;
			this.rowEdgeMaxColumnIndex = rowEdgeMaxColumnIndex;
			this.dataProvider = dataProvider;
			this.expandedRootMap = expandedRootMap;
			this.expandedLeafMap = expandedLeafMap;
			this.rootInfo = JSON.stringify({ cellKey: currentCellKey, resizeKey });
			this._initializeExpandContext();
		}

		/**
		 * Process data for the currentIndex.
		 *
		 * @param {integer} currentIndex represents either an index to a row on column edge headers or an index to a column on row edge headers
		 * @param {function} getDataForCrosstabCell the callback that returns the value to be process to deternine whether the data is a child of the expanded cell
		 * @param {object} context contains context needed to process the data
		 * @param {object} context.expandedChildrenIndices map of row or column expanded children indices
		 * @param {object} context.parentUseValueIds map of parent use value ids
		 * @return {object} contains result of the process data call
		 */
		processData ({ currentIndex, getDataForCrosstabCell, context }) {
			let isDone = false;
			let isExpandedCell = false;
			let useValueToReturnId = null;
			const { expandedChildrenIndices, parentUseValueIds } = context;
			if (!expandedChildrenIndices[currentIndex]) {
				const { parentUseValueId, currentCellKey } = this.cellToBeExpanded;
				const { useValueId, parentUseValueId: parentUseValueIdToCheck, cellKey: childCellKey } = getDataForCrosstabCell({ currentIndex: currentIndex });
				if (parentUseValueIdToCheck) {
					if (parentUseValueIds[parentUseValueIdToCheck]) {
						isExpandedCell = true;
					} else if (parentUseValueIdToCheck === parentUseValueId) {
						// encounter a cell that share the same parent use value as the expanded cell ==> means this cell is not part of the expansion so we are done
						isDone = true;
					}
				}
				if (isExpandedCell) {
					expandedChildrenIndices[currentIndex] = true;
					if(!this.expandedLeafMap.has(childCellKey)) {
						this.expandedLeafMap.set(childCellKey, [this.rootInfo]);
					}
					if(!this.expandedLeafMap.get(childCellKey).includes(this.rootInfo)) {
						this.expandedLeafMap.get(childCellKey).push(this.rootInfo);
					}
					if(!this.expandedRootMap.has(currentCellKey)){
						this.expandedRootMap.set(currentCellKey, [childCellKey]);
					}
					useValueToReturnId = useValueId;
				}
			}
			return { isExpandedCell, isDone, useValueId: useValueToReturnId };
		}

		/**
		 * Returns the starting column index for the topmost row headers or the starting row index for the leftmost column headers
		 *
		 * @return {integer} the starting index
		 */
		getInitialStartIndex () {
			return this.initialStartIndex;
		}

		/**
		 * Returns the expanded cell use value id
		 *
		 * @return {integer} the expanded cell use value id
		 */
		getExpandedCellUseValueId () {
			return this.cellToBeExpanded.useValueId;
		}

		_initializeExpandContext () {
			const { currentRow, currentCol, colSpan, rowSpan } = this.cellToBeExpanded;
			if (this.isRowEdge) {
				this.initialStartIndex = currentCol + colSpan + 1;
				this.endIndex = this.rowEdgeMaxColumnIndex;
				this.getDataForCrosstabCell = ({ currentIndex }) => {
					const dataValue = this.dataProvider.getValue(currentRow, currentIndex);
					return {
						currentRow,
						currentCol: currentIndex,
						cellKey: `${currentRow} ${currentIndex}`,
						dataValue,
						useValueId: this.crosstabGridCellRenderers.getDataCellValue(dataValue, USE_VALUE_ID),
						parentUseValueId: this.crosstabGridCellRenderers.getDataCellValue(dataValue, PARENT_USE_VALUE_ID)
					};
				};
			} else {
				this.initialStartIndex = currentRow + rowSpan + 1;
				this.endIndex = this.columnEdgeMaxRowIndex;
				this.getDataForCrosstabCell = ({ currentIndex }) => {
					const dataValue = this.dataProvider.getValue(currentIndex, currentCol);
					return {
						currentRow: currentIndex,
						currentCol,
						cellKey: `${currentIndex} ${currentCol}`,
						dataValue,
						useValueId: this.crosstabGridCellRenderers.getDataCellValue(dataValue, USE_VALUE_ID),
						parentUseValueId: this.crosstabGridCellRenderers.getDataCellValue(dataValue, PARENT_USE_VALUE_ID)
					};
				};
			}
		}
	}

	return class GridCellExpansionHelper {
		constructor({ content, gridInfo, crosstabGridCellRenderers }) {
			this.content = content;
			this.gridInfo = gridInfo;
			this.crosstabGridCellRenderers = crosstabGridCellRenderers;
			this._loadExpandInfo();
		}

		getDataItemInfoBySlotId ({ slotId }) {
			return this._expandInfo[slotId];
		}

		getFixedCellExpandInfo({ cellInfo, isRowEdge, gridCellProps }) {
			this.isDone = false;
			const { isExpandable, pivotTable } = this.gridInfo;
			const { dataValue, currentRow, currentCol, isSummary } = cellInfo;
			let expandOptions = {};
			if (isExpandable && !isSummary) {
				const cachedLevelInfo = pivotTable.getLevelInfo();
				let dataItemInfo;
				let levelInfo;
				let position;
				let index;
				let slotId;
				if (isRowEdge) {
					index = currentRow;
					position = 'top';
					levelInfo = cachedLevelInfo.row[currentRow];
					// Location is on rows for leftmost cells. The slot id for left most cells is CrosstabConstants.EDGE_SLOT_IDS.COLUMN
					slotId = CrosstabConstants.EDGE_SLOT_IDS.COLUMN;
				} else {
					index = currentCol;
					position = 'left';
					levelInfo = cachedLevelInfo.col[currentCol];
					// Location is on rows for topmost cells. The slot id for topmost cells is CrosstabConstants.EDGE_SLOT_IDS.ROW
					slotId = CrosstabConstants.EDGE_SLOT_IDS.ROW;
				}
				dataItemInfo = this._getCachedDataItemInfoByIndex({ expandInfo: this._expandInfo[slotId], index });
				expandOptions = this._getExpandOptions({ dataValue, dataItemInfo, levelInfo, position, gridCellProps }) || {};
			}
			if (expandOptions.isExpanded) {
				expandOptions.numberOfChildren = this._getFixedCellExpandChildren({ cellInfo, isRowEdge });
				expandOptions.hasChildren = expandOptions.numberOfChildren > 0;
			}
			return expandOptions;
		}

		_getExpandOptions({ dataValue, dataItemInfo, levelInfo, position, gridCellProps }) {
			const value = this.crosstabGridCellRenderers.getDataCellValue(dataValue, USE_VALUE);
			if (dataItemInfo && !dataItemInfo.isProperty && value && value.length) {
				const useValues = value.split(',');
				// in nested scenario, the useValue of the cell will contain all the edge useValues, the last one will be the one we need
				const useValue = useValues[useValues.length - 1];
				const isExpanded = !!(dataItemInfo && dataItemInfo.expandMap && dataItemInfo.expandMap[useValue]);
				let isExpansionLineageHintSupported = true;
				if (gridCellProps && gridCellProps.isExpansionLineageHintSupported === false) {
					// Only force isExpansionLineageHintSupported to false if it has been explicitly set to
					// false (not falsy)
					isExpansionLineageHintSupported = false;
				}
				return {
					dataItemInfo,
					parentUseValue: this.crosstabGridCellRenderers.getDataCellValue(dataValue, PARENT_USE_VALUE),
					currentUseValue: useValue,
					levelNumber: this.crosstabGridCellRenderers.getDataCellValue(dataValue, 'levelNumber'),
					isExpanded,
					isExpansionLineageHintSupported,
					position,
					rootLevel: levelInfo && levelInfo.rootLevelnumber
				};
			}
			return {
				isExpanded: false,
				hasChildren: false,
				numberOfChildren: 0,
				isExpansionLineageHintSupported: true
			};
		}

		_getFixedCellExpandChildren({ cellInfo, isRowEdge }) {
			const gridCellExpandContext = new GridCellExpandContext({ cellInfo, gridInfo: this.gridInfo, crosstabGridCellRenderers: this.crosstabGridCellRenderers, isRowEdge });
			const startIndex = gridCellExpandContext.getInitialStartIndex();
			this.context = {
				expandedChildrenIndices: {},
				parentUseValueIds: {
					[gridCellExpandContext.getExpandedCellUseValueId()]: true
				}
			};
			return this._recursiveCalculateNumOfExpandedChildren({ gridCellExpandContext, startIndex, context: this.context });
		}

		_recursiveCalculateNumOfExpandedChildren ({ gridCellExpandContext, startIndex, context }) {
			// This version does not rely on levelNumber since member-based hierarchy do not have level number
			let numberOfChildren = 0;
			const { endIndex, getDataForCrosstabCell } = gridCellExpandContext;
			while (startIndex <= endIndex) {
				if (this.isDone) {
					break;
				}
				const { isExpandedCell, isDone, useValueId } = gridCellExpandContext.processData ({ currentIndex: startIndex, getDataForCrosstabCell, context });
				if (!this.isDone && isDone) {
					this.isDone = true;
				}
				if (isExpandedCell) {
					context.parentUseValueIds[useValueId] = true;
					numberOfChildren += 1 + this._recursiveCalculateNumOfExpandedChildren({
						gridCellExpandContext,
						startIndex: startIndex + 1,
						context
					});
					// Increment index to next row or next col
					++startIndex;
				} else {
					break;
				}
			}
			return numberOfChildren;
		}

		_getCachedDataItemInfoByIndex( { expandInfo, index }) {
			return expandInfo && expandInfo[index];
		}

		_loadExpandInfo() {
			this._expandInfo = {};
			const visualization = this.content && this.content.getFeature('Visualization');
			const slots = visualization && visualization.getSlots();
			const slotIds = [CrosstabConstants.EDGE_SLOT_IDS.ROW, CrosstabConstants.EDGE_SLOT_IDS.COLUMN];
			slotIds.forEach(slotId => {
				const slot = slots.getSlot(slotId);
				const dataItemList = slot && slot.getDataItemList();
				if (Array.isArray(dataItemList)) {
					const dataItemInfo = this._expandInfo[slotId] = {};
					dataItemList.forEach((dataItem, index) => {
						const { expandMap, maxDepth } = this._getExpandInfo({ dataItem, maxDepth: 0, expandMap: {} });
						dataItemInfo[index] = {
							dataitemId: dataItem.getColumnId(),
							dataitemUniqueId: dataItem.getId(),
							expandMap,
							maxDepth,
							isProperty: dataItem.getMetadataColumn().isProperty()
						};
					});
				}
			});
		}

		/**
		 * get expandInfo from the dataItem
		 * @param {object} dataItem
		 * @param {integer} maxDepth
		 * @param {object} expandMap
		 * @returns {Object} maxDepth and expandMap
		 */
		_getExpandInfo({ dataItem, maxDepth, expandMap = {} }) {
			maxDepth = maxDepth || 0;
			const union = dataItem.getUnion();
			const dataItemList = union.getDataItemList();
			if (dataItemList.length > 0) {
				maxDepth++;
				dataItemList.forEach(newDataItem => {
					const nestedChildren = newDataItem.getDrillDownValue();
					expandMap[nestedChildren] = true;
					const nestedUnion = newDataItem.getUnion();
					const nestedDataItemList = nestedUnion.getDataItemList();
					if (nestedDataItemList.length > 0) {
						const { maxDepth: tmpMaxDepth } = this._getExpandInfo({ dataItem: newDataItem, maxDepth, expandMap });
						if (tmpMaxDepth > maxDepth) {
							maxDepth = tmpMaxDepth;
						}
					}
				});
			}
			return { maxDepth, expandMap };
		}
	};
});
